/** One-shot fullscreen confetti burst. Imperative API — call
 *  `fireConfetti()` from anywhere and a fixed-position overlay renders
 *  ~50 kawaii-tinted particles drifting upward + outward, then cleans up
 *  after the animation finishes. No React state to coordinate. */

interface ConfettiOptions {
  /** Pixels from the bottom of the viewport the particles emit from.
   * Defaults to roughly the bottom-center quadrant. */
  emitFromBottom?: number;
  /** Override the default particle count. */
  count?: number;
}

const KAWAII_COLORS = [
  'var(--color-primary-emphasized)',
  'var(--color-primary-main)',
  'var(--color-secondary-emphasized)',
  'var(--color-secondary-main)',
  'var(--color-tertiary-emphasized)',
  'var(--color-tertiary-main)',
  'var(--color-quaternary-emphasized)',
] as const;

/** Fire a one-shot confetti burst across the viewport. Honors
 *  `prefers-reduced-motion` — a user with reduced motion gets no
 *  animation. Returns a cleanup function in case the caller wants to
 *  abort the burst mid-flight. */
export const fireConfetti = (options: ConfettiOptions = {}): (() => void) => {
  if (typeof document === 'undefined') return () => {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return () => {};
  }

  const { emitFromBottom = window.innerHeight * 0.4, count = 60 } = options;
  // Single container so a single removal cleans up the whole burst.
  const container = document.createElement('div');
  container.setAttribute('aria-hidden', 'true');
  container.style.position = 'fixed';
  container.style.inset = '0';
  container.style.pointerEvents = 'none';
  container.style.zIndex = '700'; // sits above modals + toasts
  container.style.overflow = 'hidden';

  for (let i = 0; i < count; i++) {
    const particle = document.createElement('span');
    // Random horizontal start within the central 60% of the viewport.
    const startX = window.innerWidth * (0.2 + Math.random() * 0.6);
    const startY = window.innerHeight - emitFromBottom;
    // Drift angle skewed upward (60°–120° toward up), with horizontal
    // spread proportional to the distance.
    const angle = (Math.random() * 60 + 60) * (Math.PI / 180);
    const distance = 220 + Math.random() * 220;
    const tx = Math.cos(angle) * distance * (Math.random() > 0.5 ? 1 : -1);
    const ty = -Math.sin(angle) * distance;
    const size = 4 + Math.random() * 6;
    const color = KAWAII_COLORS[Math.floor(Math.random() * KAWAII_COLORS.length)];
    const duration = 1.4 + Math.random() * 0.8;

    particle.style.position = 'absolute';
    particle.style.left = `${startX}px`;
    particle.style.top = `${startY}px`;
    particle.style.width = `${size}px`;
    particle.style.height = `${size}px`;
    particle.style.borderRadius = '9999px';
    particle.style.background = color as string;
    particle.style.opacity = '0';
    particle.style.setProperty('--kawaii-tx', `${tx}px`);
    particle.style.setProperty('--kawaii-ty', `${ty}px`);
    particle.style.animation = `kawaii-confetti ${duration}s ease-out ${Math.random() * 0.15}s forwards`;
    container.appendChild(particle);
  }

  document.body.appendChild(container);
  // Remove the container after the longest particle finishes. The
  // particle animations are `forwards`, so they'd otherwise sit at their
  // end state (opacity 0) but still occupy DOM nodes.
  const cleanup = window.setTimeout(() => {
    container.remove();
  }, 2500);

  return () => {
    window.clearTimeout(cleanup);
    container.remove();
  };
};
